import { ToastMessage } from '@/components'
import PropTypes from 'prop-types'
import { useCallback, useState, useRef } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useLinkToken } from '../pages/hooks/use-link-token'
import { syncPlaidItems } from '../api/payment/plaid'
import PlaidLoadingScreen from './plaid-loading-screen'
import { usePaymentMethods } from '../pages/payments-page/hooks/use-payment-methods'
import { useQueryClient } from '@tanstack/react-query'
import PlaidTransactionPickerModal from '../pages/plaid-transaction-picker/plaid-transaction-picker-modal'
import { getPlaidLoanTransactions } from '../api/transactions/loans'

// Main Plaid Link component
const PlaidLink = ({
  onSuccess,
  onExit,
  children,
  updateItemId, // Optional itemId for update link
  authOnly = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const finishingOAuth = searchParams.has('oauth_state_id')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isProcessingTransactions, setIsProcessingTransactions] = useState(false)
  const [showTransactionPicker, setShowTransactionPicker] = useState(false)
  const [transactionPickerData, setTransactionPickerData] = useState(null)
  const queryClient = useQueryClient()

  // Get link token using the hook with OAuth state
  const { data: linkTokenData, isFetching: isFetchingLinkToken } = useLinkToken({
    finishingOAuth,
    itemId: updateItemId,
    authOnly,
  })

  // Store detection result from exchangePublicToken response
  const detectionResultRef = useRef(null)

  // Callback for when account is linked - check detection result
  const handleAccountLinked = useCallback(async (account) => {
    // Stop showing processing message
    setIsProcessingTransactions(false)

    // Check if we have a detection result from the exchangePublicToken response
    const detectionResult = detectionResultRef.current

    if (!account.monitorable || !account.plaidItemId) {
      // Not a monitored account - show success toast immediately
      toast.custom(() => (
        <ToastMessage
          success
          text="Account added successfully"
          description="Your new account is now active and can be used both to pay your balance and as a default payment method."
        />
      ))
      detectionResultRef.current = null
      return
    }

    // If no detection result, show success toast (detection may have happened but result not available)
    if (!detectionResult) {
      toast.custom(() => (
        <ToastMessage
          success
          text="Account added successfully"
          description="Your new account is now active and can be used both to pay your balance and as a default payment method."
        />
      ))
      return
    }

    // Check detection result
    if (detectionResult.success) {
      // Detection successful - show normal success toast
      toast.custom(() => (
        <ToastMessage
          success
          text="Account added successfully"
          description="Your new account is now active and can be used both to pay your balance and as a default payment method."
        />
      ))
    } else {
      // Detection failed - fetch transactions and check if any exist
      try {
        let transactions = await getPlaidLoanTransactions({
          plaidItemId: account.plaidItemId,
        })

        // Only show modal if there are transactions available
        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
          setTransactionPickerData({
            bankAccountId: account.id,
            plaidItemId: account.plaidItemId,
            transactions, // Pass transactions to modal
          })
          setShowTransactionPicker(true)
        } else {
          // No transactions available - skip modal and show success toast
          toast.custom(() => (
            <ToastMessage
              success
              text="Account added successfully"
              description="Your new account is now active and can be used both to pay your balance and as a default payment method."
            />
          ))
        }
      } catch (error) {
        // If fetching transactions fails, show success toast instead of modal
        console.error('Error fetching transactions for picker:', error)
        toast.custom(() => (
          <ToastMessage
            success
            text="Account added successfully"
            description="Your new account is now active and can be used both to pay your balance and as a default payment method."
          />
        ))
      }
    }

    // Clear the stored result
    detectionResultRef.current = null
  }, [])

  // Get publicTokenMutate from usePaymentMethods for new connections
  const { publicTokenMutate, isPaymentMethodAdding } = usePaymentMethods({ 
    monitor: true,
    onAccountLinked: handleAccountLinked,
  })


  const removeOAuthStateId = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('oauth_state_id') // Remove only this param
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // Update success handler (for reconnecting existing accounts)
  const updateOnSuccess = useCallback(async () => {
    try {
      setIsUpdating(true)

      // Refresh the item status to ACTIVE by syncing all Plaid items
      await syncPlaidItems()
      console.log('Successfully re-synced Plaid items after update link')

      toast.custom(() => (
        <ToastMessage
          success
          text="Account successfully reconnected"
          description="You've successfully reconnected your payment account. You will continue to earn rewards on your vehicle lease and loan payments"
        />
      ))
    } catch (error) {
      console.error('Error refreshing Plaid item status:', error)
      throw error
    } finally {
      setIsUpdating(false)
    }
  }, [])

  // Default success handler (for connecting new accounts)
  const defaultOnSuccess = useCallback(
    async (public_token, payload) => {
      try {
        const result = await publicTokenMutate({ publicToken: public_token, payload })
        
        // Store detection result for later use in handleAccountLinked
        if (result?.loanLeaseDetection !== undefined && result.loanLeaseDetection !== null) {
          detectionResultRef.current = result.loanLeaseDetection
          // Show processing message since detection was performed (happened during API call)
          setIsProcessingTransactions(true)
        }
        
        // The loading screen will show until the polling completes
        // Detection result will be checked via handleAccountLinked callback
      } catch (error) {
        console.error('Default onSuccess error:', error)
        setIsProcessingTransactions(false)
        throw error
      }
    },
    [publicTokenMutate]
  )

  const handleSuccess = useCallback(
    async (public_token, payload) => {
      try {
        // Use update handler if updateItemId is provided, otherwise use default
        if (updateItemId) {
          await updateOnSuccess(public_token, payload)
        } else {
          await defaultOnSuccess(public_token, payload)
        }

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(public_token, payload)
        }

        // Invalidate plaid-items query to update the persistent header banner
        queryClient.invalidateQueries({ queryKey: ['plaid-items'] })

        if (finishingOAuth) {
          removeOAuthStateId()
        }
      } catch (error) {
        console.log(error)
        let description =
          "We're unable to link your account at this time. Please try again later or contact support at help@fasten.cc."
        if (error.statusCode === 409) {
          description = error.message
        }
        toast.custom(() => <ToastMessage text="Something went wrong" description={description} />)
      }
    },
    [onSuccess, updateItemId, updateOnSuccess, defaultOnSuccess, finishingOAuth, removeOAuthStateId, queryClient]
  )

  const handleExit = useCallback(
    (err, metadata) => {
      if (err != null) {
        toast.custom(() => (
          <ToastMessage
            text="Something went wrong"
            description="Unfortunately, we encountered an unexpected issue. Please try again, or use the 'Contact Support' button below for assistance. We're here to help you resolve this as smoothly as possible."
          />
        ))
      }
      if (finishingOAuth) {
        removeOAuthStateId()
      }
      if (onExit) {
        onExit(err, metadata)
      }
    },
    [onExit, finishingOAuth, removeOAuthStateId]
  )

  const { open, ready } = usePlaidLink({
    token: linkTokenData?.link_token,
    receivedRedirectUri: finishingOAuth ? window.location.href : undefined,
    onSuccess: handleSuccess,
    onExit: handleExit,
  })

  return (
    <>
      {children({
        open,
        ready: ready && !isFetchingLinkToken,
      })}

      {/* Show loading screen when connecting and using default handlers */}
      {(isUpdating || isPaymentMethodAdding || isProcessingTransactions) && (
        <PlaidLoadingScreen isProcessingTransactions={isProcessingTransactions} />
      )}

      {/* Show transaction picker modal if detection failed */}
      {showTransactionPicker && transactionPickerData && (
        <PlaidTransactionPickerModal
          isOpen={showTransactionPicker}
          onClose={() => setShowTransactionPicker(false)}
          bankAccountId={transactionPickerData.bankAccountId}
          plaidItemId={transactionPickerData.plaidItemId}
          transactions={transactionPickerData.transactions}
        />
      )}
    </>
  )
}

PlaidLink.propTypes = {
  onSuccess: PropTypes.func, // Optional
  onExit: PropTypes.func, // Optional
  children: PropTypes.func.isRequired,
  updateItemId: PropTypes.string,
  authOnly: PropTypes.bool,
}

export default PlaidLink
